import React, { useState } from 'react';
import { Income } from '../types';
import { RowActions, RowActionCell } from './RowActions';
import { toDayKey, toLocalDay } from '../utils/finance/dates';
import {
  PageHeader, SectionHeader, EmptyState, FormScreen, useFormScreenScroll,
  Button, Tag, List, Field, Input, Select, TYPE, cn,
} from './ui';

type IncomeDraft = {
  name: string;
  amount: number;
  payDay?: number;
  frequency: 'monthly' | 'biweekly' | 'weekly' | 'one-time';
  isActive: boolean;
  depositDate?: string;
  biweeklyStartDate?: string;
  startDate?: string;
  endDate?: string;
};

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** One field set for both add and edit — previously two near-identical blocks. */
const IncomeFields: React.FC<{
  value: IncomeDraft;
  onChange: (next: IncomeDraft) => void;
  idPrefix: string;
}> = ({ value, onChange, idPrefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
    <Field label="Income name" htmlFor={`${idPrefix}-name`}>
      <Input
        id={`${idPrefix}-name`}
        type="text"
        required
        value={value.name}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
      />
    </Field>

    <Field label="Amount" hint="Per deposit, not per month." htmlFor={`${idPrefix}-amount`}>
      <Input
        id={`${idPrefix}-amount`}
        type="number"
        required
        step="0.01"
        value={value.amount}
        onChange={(e) => onChange({ ...value, amount: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 })}
      />
    </Field>

    <Field label="Frequency" htmlFor={`${idPrefix}-frequency`}>
      <Select
        id={`${idPrefix}-frequency`}
        value={value.frequency}
        onChange={(e) => onChange({ ...value, frequency: e.target.value as IncomeDraft['frequency'] })}
      >
        <option value="monthly">Monthly</option>
        <option value="biweekly">Bi-weekly</option>
        <option value="weekly">Weekly</option>
        <option value="one-time">One-time deposit</option>
      </Select>
    </Field>

    {value.frequency === 'one-time' ? (
      <Field label="Deposit date" htmlFor={`${idPrefix}-deposit`}>
        <Input
          id={`${idPrefix}-deposit`}
          type="date"
          required
          value={value.depositDate || ''}
          onChange={(e) => onChange({ ...value, depositDate: e.target.value })}
        />
      </Field>
    ) : value.frequency === 'biweekly' ? (
      <>
        {/* No weekday picker here. The anchor below fixes the weekday, the
            cadence and the start all at once — a separate "Pay day" was input
            nothing read, and it could disagree with the date that does. */}
        <Field
          label="First payday"
          hint="Every other week counts forward from this date, which also sets the weekday and when the source starts."
          htmlFor={`${idPrefix}-biweekly`}
        >
          <Input
            id={`${idPrefix}-biweekly`}
            type="date"
            required
            value={value.biweeklyStartDate || ''}
            onChange={(e) => onChange({ ...value, biweeklyStartDate: e.target.value })}
          />
        </Field>
      </>
    ) : (
      <Field
        label="Pay day"
        hint={value.frequency === 'monthly' ? 'Clamped to the month length.' : undefined}
        htmlFor={`${idPrefix}-payday`}
      >
        {value.frequency === 'weekly' ? (
          <Select
            id={`${idPrefix}-payday`}
            value={value.payDay}
            onChange={(e) => onChange({ ...value, payDay: parseInt(e.target.value) || 0 })}
          >
            {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </Select>
        ) : (
          <Input
            id={`${idPrefix}-payday`}
            type="number"
            required
            min="1"
            max="31"
            value={value.payDay}
            onChange={(e) => onChange({ ...value, payDay: e.target.value === '' ? 1 : parseInt(e.target.value) || 1 })}
          />
        )}
      </Field>
    )}

    {value.frequency !== 'one-time' && (
      <>
        {/* Monthly and weekly only. A biweekly source's cadence is counted
            from "First payday", so that date already IS its start — offering
            a second one would be two fields for one fact. */}
        {value.frequency !== 'biweekly' && (
          <Field
            label="Start date"
            hint="Optional — leave blank if it is already paying out."
            htmlFor={`${idPrefix}-start`}
          >
            <Input
              id={`${idPrefix}-start`}
              type="date"
              value={value.startDate || ''}
              onChange={(e) => onChange({ ...value, startDate: e.target.value })}
            />
          </Field>
        )}
        <Field label="End date" hint="Optional — leave blank to continue indefinitely." htmlFor={`${idPrefix}-end`}>
          <Input
            id={`${idPrefix}-end`}
            type="date"
            value={value.endDate || ''}
            onChange={(e) => onChange({ ...value, endDate: e.target.value })}
          />
        </Field>
      </>
    )}
  </div>
);


interface IncomeManagerProps {
  incomes: Income[];
  onAddIncome: (income: Omit<Income, 'id'>) => void;
  onUpdateIncome: (id: string, income: Partial<Income>) => void;
  onDeleteIncome: (id: string) => void;
}

// Helper function to parse date string in local timezone
/** Which frequencies carry a start date of their own. Biweekly does not: its
 *  "First payday" anchors the cadence and is already the earliest payday it can
 *  produce. One-time has a deposit date, which is its whole window. Writing
 *  null for the others matters — switching a monthly source to biweekly would
 *  otherwise leave a start date behind that nothing displays and that quietly
 *  clips the schedule. */
const startDateApplies = (frequency: string) => frequency === 'monthly' || frequency === 'weekly';

const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

// Helper function to format a UTC date to local date string (without timezone shift)
const formatUTCDate = (date: Date): string => {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()).toLocaleDateString();
};

/** Source / frequency / schedule / ends / monthly / amount / status. Below md
 *  the middle four drop away. */
const INC_COLS =
  'grid grid-cols-[1.7fr_1fr_.9fr] md:grid-cols-income gap-x-4 items-center';

/** Is this source actually paying out today?
 *
 *  Both starts count, and they mean different things: `startDate` is when the
 *  source begins, `biweeklyStartDate` anchors the fortnightly cadence and is
 *  also the earliest payday it can produce. A source is live once it is past
 *  whichever of them applies. */
const isLiveNow = (i: Income, today: Date): boolean => {
  if (!i.isActive || i.frequency === 'one-time') return false;
  if (i.startDate && toLocalDay(i.startDate) > today) return false;
  if (i.biweeklyStartDate && toLocalDay(i.biweeklyStartDate) > today) return false;
  if (i.endDate && toLocalDay(i.endDate) < today) return false;
  return true;
};

const IncCol: React.FC<{ label: string; align?: string; deskOnly?: boolean }> = ({
  label, align = 'text-left', deskOnly,
}) => (
  <div className={cn(TYPE.sectionLabel, 'text-ink-muted', align, deskOnly && 'hidden md:block')}>
    {label}
  </div>
);

export const IncomeManager: React.FC<IncomeManagerProps> = ({
  incomes,
  onAddIncome,
  onUpdateIncome,
  onDeleteIncome
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIncomeId, setEditingIncomeId] = useState<string | null>(null);
  const [newIncome, setNewIncome] = useState<{
    name: string;
    amount: number;
    payDay?: number;
    frequency: 'monthly' | 'biweekly' | 'weekly' | 'one-time';
    isActive: boolean;
    depositDate?: string;
    biweeklyStartDate?: string;
    startDate?: string;
    endDate?: string;
  }>({
    name: '',
    amount: 0,
    payDay: 1,
    frequency: 'monthly',
    isActive: true,
    depositDate: new Date().toISOString().split('T')[0],
    biweeklyStartDate: new Date().toISOString().split('T')[0]
  });
  const [editIncome, setEditIncome] = useState<{
    name: string;
    amount: number;
    payDay?: number;
    frequency: 'monthly' | 'biweekly' | 'weekly' | 'one-time';
    isActive: boolean;
    depositDate?: string;
    biweeklyStartDate?: string;
    startDate?: string;
    endDate?: string;
  }>({
    name: '',
    amount: 0,
    payDay: 1,
    frequency: 'monthly',
    isActive: true,
    depositDate: new Date().toISOString().split('T')[0],
    biweeklyStartDate: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const incomeToAdd = {
      ...newIncome,
      depositDate: newIncome.frequency === 'one-time' && newIncome.depositDate 
        ? parseLocalDate(newIncome.depositDate) 
        : undefined,
      biweeklyStartDate: newIncome.frequency === 'biweekly' && newIncome.biweeklyStartDate 
        ? parseLocalDate(newIncome.biweeklyStartDate) 
        : undefined,
      startDate: startDateApplies(newIncome.frequency) && newIncome.startDate
        ? parseLocalDate(newIncome.startDate)
        : null,
      endDate: newIncome.frequency !== 'one-time' && newIncome.endDate 
        ? parseLocalDate(newIncome.endDate) 
        : undefined
    };
    onAddIncome(incomeToAdd);
    setNewIncome({
      name: '',
      amount: 0,
      payDay: 1,
      frequency: 'monthly',
      isActive: true,
      depositDate: new Date().toISOString().split('T')[0],
      biweeklyStartDate: new Date().toISOString().split('T')[0]
    });
    setShowAddForm(false);
  };

  const startEdit = (income: Income) => {
    rememberListScroll();
    setEditingIncomeId(income.id);
    setEditIncome({
      name: income.name,
      amount: income.amount,
      payDay: income.payDay,
      frequency: income.frequency,
      isActive: income.isActive,
      // Read as a local day. toISOString() would report the UTC day, which is
      // the one before for any instant stored before 00:00 UTC.
      depositDate: income.depositDate ? toDayKey(toLocalDay(income.depositDate)) : toDayKey(new Date()),
      biweeklyStartDate: income.biweeklyStartDate ? toDayKey(toLocalDay(income.biweeklyStartDate)) : toDayKey(new Date()),
      startDate: income.startDate ? toDayKey(toLocalDay(income.startDate)) : '',
      endDate: income.endDate ? toDayKey(toLocalDay(income.endDate)) : ''
    });
  };

  const handleEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingIncomeId) {
      const incomeToUpdate = {
        ...editIncome,
        depositDate: editIncome.frequency === 'one-time' && editIncome.depositDate 
          ? parseLocalDate(editIncome.depositDate) 
          : undefined,
        biweeklyStartDate: editIncome.frequency === 'biweekly' && editIncome.biweeklyStartDate 
          ? parseLocalDate(editIncome.biweeklyStartDate) 
          : undefined,
        // null, not undefined: clearing the field has to reach the database as
        // a value, or the update silently keeps the old date.
        startDate: startDateApplies(editIncome.frequency) && editIncome.startDate
          ? parseLocalDate(editIncome.startDate)
          : null,
        endDate: editIncome.frequency !== 'one-time' && editIncome.endDate 
          ? parseLocalDate(editIncome.endDate) 
          : null
      };
      onUpdateIncome(editingIncomeId, incomeToUpdate);
      setEditingIncomeId(null);
    }
  };

  const cancelEdit = () => {
    setEditingIncomeId(null);
  };

  const cloneIncome = (income: Income) => {
    const clonedIncome = {
      name: `${income.name} (Copy)`,
      amount: income.amount,
      payDay: income.payDay,
      frequency: income.frequency,
      isActive: income.isActive,
      depositDate: income.depositDate,
      biweeklyStartDate: income.biweeklyStartDate,
      endDate: income.endDate
    };
    onAddIncome(clonedIncome);
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'monthly': return 'Monthly';
      case 'biweekly': return 'Bi-weekly';
      case 'weekly': return 'Weekly';
      case 'one-time': return 'One-time';
      default: return frequency;
    }
  };

  const calculateMonthlyIncome = (income: Income): number => {
    switch (income.frequency) {
      case 'monthly': return income.amount;
      case 'biweekly': return income.amount * 26 / 12;
      case 'weekly': return income.amount * 52 / 12;
      case 'one-time': return 0; // One-time deposits don't count toward monthly income
      default: return income.amount;
    }
  };

  const rememberListScroll = useFormScreenScroll(showAddForm || editingIncomeId !== null);

  const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
  /** What is actually arriving per month RIGHT NOW.
   *
   *  Summing every active recurring source is wrong here: the paychecks are
   *  sequential, not concurrent — 2026 ends in December, 2027 Q1 picks up in
   *  January, Q2-4 in March. Adding all three reported $22,642 a month against
   *  a real figure near $7,258. So the total counts only sources whose date
   *  window contains today, and one-time deposits are excluded rather than
   *  smeared across twelve months. */
  const monthlyTotal = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return incomes.filter(i => isLiveNow(i, today)).reduce(
      (sum, i) => sum + calculateMonthlyIncome(i),
      0
    );
  }, [incomes]);

  /** How many recurring sources are live today, so the footer can say what the
   *  figure covers rather than implying it covers every row. */
  const liveCount = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return incomes.filter(i => isLiveNow(i, today)).length;
  }, [incomes]);

  const describe = (income: Income) => {
    if (income.frequency === 'monthly' && income.payDay) return `Day ${income.payDay}`;
    if (income.frequency === 'weekly' && income.payDay !== undefined) return `${DAYS_SHORT[income.payDay]}s`;
    if (income.frequency === 'biweekly' && income.biweeklyStartDate) {
      // Read off the anchor, not a stored payDay: the anchor is what the
      // projection counts from, so it is the only thing that can be right.
      const anchor = toLocalDay(income.biweeklyStartDate);
      return `${DAYS_SHORT[anchor.getDay()]} from ${formatUTCDate(new Date(income.biweeklyStartDate))}`;
    }
    if (income.frequency === 'one-time' && income.depositDate) {
      return formatUTCDate(new Date(income.depositDate));
    }
    return '';
  };

  // Placed after every hook, so hook order never changes between renders.
  const editingIncome = editingIncomeId
    ? incomes.find(i => i.id === editingIncomeId) ?? null
    : null;

  if (showAddForm) {
    return (
      <FormScreen
        backLabel="Back to income"
        heading="New income source"
        sectionTitle="Details"
        hint="Amount is per deposit — the monthly equivalent is worked out for you."
        submitLabel="Add income"
        onSubmit={handleSubmit}
        onCancel={() => setShowAddForm(false)}
      >
        <IncomeFields value={newIncome} onChange={setNewIncome} idPrefix="new-income" />
      </FormScreen>
    );
  }

  if (editingIncome) {
    return (
      <FormScreen
        backLabel="Back to income"
        heading="Edit income source"
        subject={editingIncome.name}
        sectionTitle="Details"
        hint="Amount is per deposit — the monthly equivalent is worked out for you."
        submitLabel="Save changes"
        onSubmit={handleEdit}
        onCancel={cancelEdit}
      >
        <IncomeFields value={editIncome} onChange={setEditIncome} idPrefix="edit-income" />
      </FormScreen>
    );
  }

  return (
    <>
      <PageHeader
        heading="Income Sources"
        subhead="Everything that lands in the account, and how often."
        actions={
          <Button
            variant="primary"
            onClick={() => {
              rememberListScroll();
              setShowAddForm(true);
            }}
          >
            Add income
          </Button>
        }
      />

      <section className="mt-14">
        <SectionHeader label="Sources" note={`${incomes.length} total`} />
        <div className="mt-5">
          {incomes.length === 0 ? (
            <EmptyState>No income sources added yet</EmptyState>
          ) : (
            <List>
              <div className={cn(INC_COLS, 'px-4 py-2.5 bg-ground-sunk border-b border-rule-base')}>
                <IncCol label="Source" />
                <IncCol label="Frequency" deskOnly />
                <IncCol label="Schedule" deskOnly />
                <IncCol label="Ends" deskOnly />
                <IncCol label="Monthly" align="text-right" deskOnly />
                <IncCol label="Amount" align="text-right" />
                <IncCol label="Status" align="text-right" />
              </div>

              {incomes.map(income => (
                <div
                  key={income.id}
                  onClick={() => startEdit(income)}
                  className={cn(
                    INC_COLS,
                    'px-4 py-3 border-b border-rule last:border-b-0',
                    'cursor-pointer hover:bg-ground-sunk transition-colors duration-[120ms]',
                    !income.isActive && 'opacity-60'
                  )}
                >
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); startEdit(income); }}
                      className={cn(
                        TYPE.rowTitle,
                        'text-ink block max-w-full text-left truncate',
                        'hover:text-accent transition-colors duration-[120ms]'
                      )}
                    >
                      {income.name}
                    </button>
                  </div>

                  <div className="hidden md:block text-[13px] text-ink-soft">
                    {getFrequencyLabel(income.frequency)}
                  </div>
                  <div className={cn(TYPE.meta, 'hidden md:block text-ink-soft truncate')}>
                    {describe(income) || '—'}
                  </div>
                  <div className={cn(TYPE.meta, 'hidden md:block text-ink-muted')}>
                    {income.endDate ? formatUTCDate(new Date(income.endDate)) : '—'}
                  </div>
                  <div className={cn(TYPE.meta, 'hidden md:block text-ink-muted text-right')}>
                    {income.frequency === 'one-time' ? '—' : `${usd(calculateMonthlyIncome(income))}/mo`}
                  </div>

                  {/* Income is the one money that is coloured, so the thing you
                      scan for is the thing that stands out. */}
                  <div className={cn(TYPE.rowFigure, 'text-money-in text-right')}>
                    {usd(income.amount)}
                  </div>

                  <RowActionCell className="flex items-center justify-end gap-2">
                    {!income.isActive && <Tag weight="bone">Paused</Tag>}
                    <RowActions
                      label={income.name}
                      onEdit={() => startEdit(income)}
                      onClone={() => cloneIncome(income)}
                      onTogglePause={() => onUpdateIncome(income.id, { isActive: !income.isActive })}
                      isPaused={!income.isActive}
                      onDelete={() => onDeleteIncome(income.id)}
                    />
                  </RowActionCell>
                </div>
              ))}

              <div className={cn(INC_COLS, 'px-4 py-3.5 rule-total')}>
                <div className={cn(TYPE.sectionLabel, 'text-ink-muted')}>
                  {incomes.length} sources
                </div>
                <div className="hidden md:block" />
                <div className="hidden md:block" />
                <div className="hidden md:block" />
                <div className={cn(TYPE.rowFigure, 'hidden md:block text-money-in text-right')}>
                  {usd(monthlyTotal)}
                </div>
                <div className={cn(TYPE.meta, 'text-ink-muted text-right')}>
                  now, from {liveCount}
                </div>
                <div className={cn(TYPE.meta, 'text-ink-muted text-right')}>
                  {incomes.filter(i => !i.isActive).length} paused
                </div>
              </div>
            </List>
          )}
        </div>
      </section>
    </>
  );
};